import { Button, Grid, GridColumn, IFormHelperProps, Text, TextField } from 'telia-front-react';
import { observer } from 'mobx-react';
import React from 'react';
import Countdown from '../components/Countdown';
import HiddenCaptcha, { HiddenCaptchaRef } from '../components/HiddenCaptcha';
import LoginButton from '../components/LoginButton';
import useStores from '../stores/UseStores';
import { PHONE_FIELD_ID, getHelperErrorMessage, helperInErrorState, phoneFieldHelper } from '../util/FieldHelperUtil';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import RememberMe from './RememberMe';

const createAbortController = () => (typeof AbortController !== 'undefined' ? new AbortController() : undefined);

const SmsLogin = (): React.JSX.Element => {
  const {
    uiStore: { getLoginPanelGridWidth, showErrorNotice, showErrorNoticeWithErroredFields, setSelectedTab },
    translateStore: { translate },
    languageStore: { currentLanguage },
    smsLoginStore: {
      loginInitiated,
      smsInitiateLoginCredentials,
      smsLoginCredentials,
      initiateLogin,
      handleLogin,
      setPhoneNumber,
      setConfirmationCode,
      submitting,
      countdownTime,
      setCountdownTime,
      phoneNumber,
      phoneNumberError,
      confirmationCodeError,
      resetConfirmationCodeInvalid,
    },
    routeStore,
  } = useStores();
  const startLoginCaptchaRef = React.useRef<HiddenCaptchaRef>(null);
  const endLoginCaptchaRef = React.useRef<HiddenCaptchaRef>(null);
  const [numberFieldDirty, setNumberFieldDirty] = React.useState<boolean>(false);
  const [abortController, setAbortController] = React.useState(createAbortController());
  const travel = routeStore.decodedParams?.travel === 'true';
  const gridWidth = getLoginPanelGridWidth();

  const getNumberHelper = (dirty?: boolean) =>
    phoneNumberError
      ? ({ type: 'error', text: translate('panel.sms.phoneNumber.invalid'), id: PHONE_FIELD_ID } as IFormHelperProps)
      : phoneFieldHelper(translate, smsInitiateLoginCredentials.phoneNumber, dirty || numberFieldDirty);

  const tryToPrefillConfirmationCodeFromSms = async () => {
    const options = {
      otp: { transport: ['sms'] },
      signal: abortController?.signal,
    };
    if ('OTPCredential' in window) {
      try {
        const otp: any = await navigator.credentials.get(options);
        if (otp?.code) {
          setConfirmationCode(otp.code);
          endLogin();
        }
        setAbortController(createAbortController());
      } catch {
        setAbortController(createAbortController());
      }
    }
  };

  const startLogin = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    runInTeliaRumService('Autentimise algatamine (SMS)', async () => {
      event.preventDefault();
      setNumberFieldDirty(true);

      if (!getNumberHelper(true)) {
        const token = await startLoginCaptchaRef?.current?.getCaptchaAsync();
        await initiateLogin(token);
        tryToPrefillConfirmationCodeFromSms();
      } else {
        showErrorNoticeWithErroredFields('form.error', [getNumberHelper(true)]);
      }
    });
  };

  const endLogin = (event?: React.FormEvent<HTMLFormElement>): void => {
    abortController?.abort();
    setAbortController(createAbortController());
    runInTeliaRumService('Autentimise lõpule viimine (SMS)', async () => {
      event?.preventDefault();
      const token = await endLoginCaptchaRef?.current?.getCaptchaAsync();
      handleLogin(token);
    });
  };

  const handleConfirmationCodeExpired = () => {
    showErrorNotice('smsauth.buttonError.confirmationExpired');
    resetConfirmationCodeInvalid();
  };

  React.useEffect(() => () => abortController?.abort(), []);

  if (loginInitiated) {
    return (
      <form onSubmit={endLogin}>
        <Grid align={['center-sm']}>
          <GridColumn width={gridWidth}>
            <Text className="text-center">
              <p role="alert">{translate('panel.sms.confirmation', { phoneNumber: phoneNumber || smsInitiateLoginCredentials.phoneNumber })}</p>
              <p className="text-small">
                {translate('panel.sms.confirmation.time.prefix')}{' '}
                <Countdown time={countdownTime} updateTime={setCountdownTime} className="text-bold" countdownCompleteCallback={handleConfirmationCodeExpired} />{' '}
                {translate('panel.sms.confirmation.time.suffix')}
              </p>
              <TextField
                label={translate('panel.sms.code')}
                id="sms-code"
                value={smsLoginCredentials.confirmationCode}
                isInvalid={!!confirmationCodeError}
                errorMessage={confirmationCodeError ? translate(confirmationCodeError) : undefined}
                input={{ autoComplete: 'one-time-code', inputMode: 'numeric' }}
                onChange={setConfirmationCode}
                onFocus={resetConfirmationCodeInvalid}
                required={true}
              />
              <HiddenCaptcha ref={endLoginCaptchaRef} currentLanguage={currentLanguage} />
              <LoginButton isLoading={submitting} disabled={submitting} />
            </Text>
          </GridColumn>
        </Grid>
      </form>
    );
  }

  return (
    <Grid align={['center-sm']}>
      <GridColumn width={gridWidth}>
        <Text>
          <p>{travel ? translate('panel.sms.travelTitle') : translate('panel.sms.title')}</p>
          {!travel && <p className="text-small">{translate('panel.sms.description')}</p>}
          <HiddenCaptcha ref={startLoginCaptchaRef} currentLanguage={currentLanguage} />
          <form onSubmit={startLogin} id="smsAuthLogin">
            <Text>
              <Grid align={['bottom-xs']} gutter="small" noWrap={true}>
                <GridColumn width={['max']}>
                  <TextField
                    id={PHONE_FIELD_ID}
                    label={translate('panel.sms.phoneNumber')}
                    value={smsInitiateLoginCredentials.phoneNumber}
                    errorMessage={getHelperErrorMessage(getNumberHelper())}
                    onChange={(value) => setPhoneNumber(value)}
                    required={true}
                    input={{ autoComplete: 'tel-local' }}
                    isInvalid={helperInErrorState(getNumberHelper())}
                  />
                </GridColumn>
                <GridColumn width={['min']}>
                  <Button
                    isLoading={submitting}
                    disabled={submitting}
                    text={translate('panel.sms.submit.text')}
                    icon="#arrow-right"
                    attributes={{ type: 'submit' }}
                    onClick={() => setSelectedTab('sms')}
                  />
                </GridColumn>
              </Grid>
            </Text>
          </form>
          <RememberMe tabItemId="smsLoginTab" />
        </Text>
      </GridColumn>
    </Grid>
  );
};

export default observer(SmsLogin);
